/**
 * @critical PROCESSAMENTO DE TAGS DE DOCUMENTO - NÃO MODIFICAR
 * @description Processa tags especiais de documento antes do parsing de perguntas
 *
 * @purpose EVITA OVERRIDE: Esta função é CRÍTICA para processamento de links de documento.
 * Se modificada incorretamente, links podem não aparecer ou aparecer incorretamente.
 *
 * @regex CRÍTICO: /\[#!documento\]\s*(?:\(([^)]+)\))?\s*([\s\S]*?)\s*\[documento#!\]/g
 * - \[#!documento\] - Tag de abertura
 * - \s*(?:\(([^)]+)\))? - Conteúdo entre parênteses (opcional)
 * - [\s\S]*? - Conteúdo do documento (capturado)
 * - \[documento#!\] - Tag de fechamento
 *
 * @warning NÃO ALTERAR: Regex é CRÍTICO para funcionamento correto
 * @warning NÃO MODIFICAR: Lógica de substituição por link
 * @warning NÃO SUBSTITUIR: Outras funções não podem substituir esta funcionalidade
 *
 * @param message - Mensagem original do assistente
 * @param chatMessageId - ID da mensagem de chat (opcional, para buscar documentos gerados)
 * @returns Mensagem processada com links de documento
 *
 * @flow CRÍTICO:
 * 1. Identifica tags [#!documento] (...) [documento#!]
 * 2. Extrai o conteúdo entre parênteses
 * 3. Substitui por link de download real
 * 4. Retorna mensagem processada
 *
 * @example:
 * Input: "Baixe o documento [#!documento](contrato.pdf) [documento#!]"
 * Output: "Baixe o documento [Download de documento](real-link)"
 */

import { logger } from "../logger";

export const processDocumentTags = async (message: string, chatMessageId?: string): Promise<string> => {
  try {
    if (!message || typeof message !== "string") {
      return message;
    }

    // Verificar se a tag de fechamento está presente
    // Suporta dois formatos de fechamento:
    // 1. [/#!documento] - formato correto
    // 2. [documento#!] - formato atual (legado)
    const hasOpeningTag = /\[#!documento\]/.test(message);
    const hasCorrectClosingTag = /\[\/#!documento\]/.test(message);
    const hasLegacyClosingTag = /\[documento#!\]/.test(message);
    
    if (hasOpeningTag && !hasCorrectClosingTag && !hasLegacyClosingTag) {
      logger.warn(
        "[processDocumentTags] Tag de documento de abertura encontrada, mas a de fechamento está ausente. A mensagem não será processada.",
        {
          messagePreview: message.substring(0, 200) + "...",
          chatMessageId,
        },
      );
      return message;
    }

    // @critical REGEX CRÍTICO - CORRIGIDO
    // Regex para identificar tags de documento (com e sem parênteses)
    // Suporta dois formatos de fechamento:
    // 1. [#!documento](nome.pdf) [/#!documento] - formato correto
    // 2. [#!documento](nome.pdf) [documento#!] - formato legado
    const documentTagRegex = /\[#!documento\]\s*(?:\(([^)]+)\))?\s*([\s\S]*?)\s*(?:\[\/#!documento\]|\[documento#!\])/g;

    // @critical SUBSTITUTION CRÍTICO - NÃO MODIFICAR
    // Substitui tags por links de download reais
    let processedMessage = message;
    const matches = [...message.matchAll(documentTagRegex)];
    
    for (const match of matches) {
      const fullMatch = match[0];
      const documentName = match[1] ? match[1].trim() : "Documento";
      
      try {
        // Se temos chatMessageId, buscar documento gerado real
        if (chatMessageId) {
          const { generatedDocApi } = await import('@/services/api');
          
          // Buscar documentos gerados para esta mensagem
          const response = await generatedDocApi.getByChatMessage(chatMessageId);
          
          if (response && response.results && response.results.length > 0) {
            // Usar o primeiro documento gerado encontrado
            const generatedDoc = response.results[0];
            const displayName = match[1] ? match[1].trim() : "Documento";
            const downloadPlaceholder = `[#!DOWNLOAD_LINK=${generatedDoc.id}=${displayName}#!]`;
            
            // Substituir com placeholder
            processedMessage = processedMessage.replace(
              fullMatch,
              downloadPlaceholder,
            );
            continue;
          }
        }
        
        // Fallback: link mock se não encontrou documento real
        const mockLink = `#download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Substituir com link mock
        processedMessage = processedMessage.replace(
          fullMatch,
          `[Download de ${documentName}](${mockLink})`
        );
        
      } catch (error) {
        console.error("[processDocumentTags] Error processing document tag:", error);
        
        // Em caso de erro, usar link mock
        const mockLink = `#download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        processedMessage = processedMessage.replace(
          fullMatch,
          `[Download de ${documentName}](${mockLink})`
        );
      }
    }

    return processedMessage;
  } catch (error) {
    console.error("[processDocumentTags] Error processing document tags:", {
      error: error,
      message: message,
      messageType: typeof message,
      messageLength: message?.length,
      stack: error instanceof Error ? error.stack : "No stack trace",
    });
    
    // Em caso de erro, retorna a mensagem original
    return message;
  }
}; 